import React, { useState, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CallIcon from '@mui/icons-material/Call';
import { SocketContext } from '../../contexts/SocketContextProvider';
import { selectConfig } from '../../redux/slices/configSlice';
import { RootState } from '../../redux/store';

export default function Header() {
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const currentPage = useSelector((state: RootState) => state.page.currentPage);

  type SocketContextProps = {
    messages: any[];
  };

  const socketContext = useContext(SocketContext) as SocketContextProps | undefined;

  const { messages = [] } = socketContext || {};

  const config = useSelector(selectConfig);
  const formData = config?.data?.length ? config.data[0].attributes : null;

  console.log(formData);
  // hide logo broken image
  const handleImageLoad = useCallback(() => {
    setLoaded(true);
  }, [])

  // Close button handlers
  const handleCloseButtonClick = () => {
    setOpenCloseDialog(true);
  };

  const handleConfirmLeave = () => {
    setOpenCloseDialog(false);
    window.close();
  };

  const handleCancelLeave = () => {
    setOpenCloseDialog(false);
  };

  // Call button handlers
  const handleCallButtonClick = () => {
    setOpenCallDialog(true);
  };

  const handleConfirmCall = () => {
    setOpenCallDialog(false);

    const currentUrl = new URL(window.location.href);

    const pathParts = currentUrl.pathname.split('/').filter(part => part); // Split path into parts
    const lang = pathParts[1];
    let brand = pathParts[2];
    const sParam = currentUrl.searchParams.get('s');
    const level = currentUrl.searchParams.get('level');
    if (brand == 'dafabet') {
      brand = 'mc-desktop';
    }


    // Construct the new URL
    let navigateUrl = `https://www.cs-livechat.com/ccaas/v2/call/${brand}?lang=${lang}`;
    if (sParam) {
      navigateUrl += `&s=${sParam}`;
    }
    if (level) {
      navigateUrl = navigateUrl + "&level=" + level;
    }

    window.location.href = navigateUrl;
  };

  const handleCancelCall = () => {
    setOpenCallDialog(false);
  };

  const printChat = () => {
    const newWindow = window.open('', 'newwin', 'width=600, height=750');
    if (!newWindow) {
      alert('Unable to open a new window. Please allow pop-ups and try again.');
      return;
    }

    messages.forEach((message) => {
      const isEvent = (message.senderType === "PARTICIPANT_ADDED") || (message.senderType === "PARTICIPANT_REMOVED");
      let timeStamp = new Date(message.receivedAt).getTime();

      if (isEvent) {
        timeStamp = message.lastUpdatedTimestamp;
      }

      const formattedDate = new Date(timeStamp).toLocaleString();

      let participantText = "";
      if (isEvent) {
        if (message.senderType === "PARTICIPANT_ADDED") {
          participantText = "Agent joined";
        } else if (message.senderType === "PARTICIPANT_REMOVED") {
          participantText = "Agent left";
        }
      } else {
        participantText = message.senderParticipantType || "Unknown";
      }

      newWindow.document.write(`<p><span style="font-weight:bold">${participantText}</span> at ${formattedDate}</p>`);
      newWindow.document.write(`<p>${message.body.elementText.text}</p>`);
    });

    newWindow.document.close();
    newWindow.focus();

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      setTimeout(() => {
        newWindow.print();
      }, 1000);
    } else {
      newWindow.print();
      setTimeout(() => {
        newWindow.close();
      }, 1000);
    }
  };



  return (
    <Box
      pl={2}
      pr={2}
      sx={{
        backgroundColor: 'primary.main',
        padding: '16px',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        sx={{
          width: '30%',
          maxWidth: '200px',
        }}
        alt={formData?.Template?.BrandLogoMobile?.data?.attributes?.alternativeText ? formData?.Template?.BrandLogoMobile?.data?.attributes?.alternativeText : "Logo image"}
        src={formData?.Template?.BrandLogoMobile?.data?.attributes?.url ? formData?.Template?.BrandLogoMobile?.data?.attributes?.url : ""}
        style={{ visibility: loaded ? 'visible' : 'hidden', opacity: loaded ? 1 : 0 }}
        onLoad={handleImageLoad}
      />

      <Box
        sx={{
          display: 'flex',
          gap: 1,
        }}
      >

        {currentPage === 'chat' && (
          <>
            <Button
              variant="outlined"
              sx={{
                color: 'secondary.light',
                border: '3px solid',
                borderRadius: '50%',
                minWidth: 'auto',
                width: '35px',
                height: '35px',
              }}
              onClick={printChat}
              title={formData?.Template?.PrintTooltip}
            >
              <PrintIcon />
            </Button>

            {(formData.CallUs?.EnableCallUsButton) ?
              <Button
                variant="outlined"
                sx={{
                  color: 'secondary.light',
                  border: '3px solid',
                  borderRadius: '50%',
                  minWidth: 'auto',
                  width: '35px',
                  height: '35px',
                }}
                onClick={handleCallButtonClick}
                title={formData?.Template?.CallUsTooltip ?? "call"}
              >
                <CallIcon />
              </Button>
              : null}
          </>
        )}

        <Button
          variant="outlined"
          sx={{
            color: 'secondary.light',
            border: '3px solid',
            borderRadius: '50%',
            minWidth: 'auto',
            width: '35px',
            height: '35px',
          }}
          onClick={handleCloseButtonClick}
          title={formData?.Template?.PrintTooltip ?? "close"}
        >
          <CloseIcon />
        </Button>
      </Box>

      {/* Close Confirmation Dialog */}
      <Dialog
        open={openCloseDialog}
        onClose={handleCancelLeave}
        aria-labelledby="close-dialog-title"
        aria-describedby="close-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            width: '400px', // Set a consistent width
          },
        }}
      >
        <DialogTitle id="close-dialog-title">{formData?.LeaveChat?.LeaveChatConfirmationTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="close-dialog-description">
            {formData?.LeaveChat?.LeaveChatConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave} color="primary">
            {formData?.LeaveChat?.No}
          </Button>
          <Button onClick={handleConfirmLeave} color="secondary" autoFocus>
            {formData?.LeaveChat?.Yes}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Confirmation Dialog */}
      <Dialog
        open={openCallDialog}
        onClose={handleCancelCall}
        aria-labelledby="call-dialog-title"
        aria-describedby="call-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            width: '400px', // Set a consistent width
          },
        }}
      >
        <DialogTitle id="call-dialog-title">{formData?.LeaveChat?.CallConfirmationTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="call-dialog-description">
            {formData?.LeaveChat?.LeaveChatConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCall} color="primary">
            {formData?.LeaveChat?.No}
          </Button>
          <Button onClick={handleConfirmCall} color="secondary" autoFocus>
            {formData?.LeaveChat?.Yes}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
